import { memo, forwardRef } from 'react';
import { cn } from '@/lib/utils';

interface FormFieldProps {
  id: string;
  name: string;
  label: string;
  type?: string;
  value: string;
  placeholder: string;
  autoComplete?: string;
  maxLength?: number;
  error?: string;
  touched?: boolean;
  required?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const FormField = memo(forwardRef<HTMLInputElement, FormFieldProps>(({
  id,
  name,
  label,
  type = 'text',
  value,
  placeholder,
  autoComplete,
  maxLength,
  error,
  touched,
  required = false,
  onChange,
  onBlur,
}, ref) => {
  const hasError = touched && error;
  
  const inputClasses = cn(
    'w-full px-4 py-3 rounded-xl bg-muted/50 border outline-none transition-all text-foreground placeholder:text-muted-foreground',
    hasError
      ? 'border-destructive focus:border-destructive focus:ring-2 focus:ring-destructive/20'
      : 'border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
  );

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground mb-2">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <input
        type={type}
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        maxLength={maxLength}
        ref={ref}
        className={inputClasses}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {hasError && (
        <p id={`${id}-error`} className="mt-1 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}));

FormField.displayName = 'FormField';

export default FormField;
